import { useState } from 'react'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { optionalPhone, isMaskSafePhone } from '@/lib/phone'
import {
	Form, FormControl, FormDialog, FormField, FormItem, FormLabel, FormMessage,
	Input, PhoneInput, TextField,
} from '@rentflowdev/ui'
import {
	useConfirmEmailChangeMutation,
	useConfirmPhoneChangeMutation,
	useInitEmailChangeMutation,
	useInitPhoneChangeMutation,
	useUpdateTelegramMutation,
} from '@/api'
import { usePersistedForm } from '@/lib/use-persisted-form'

type Step =
	| { kind: 'edit' }
	/**
	 * `nextEmail` — почта, которую ещё предстоит подтвердить.
	 *
	 * Каналы подтверждаются по одному, и при смене телефона и почты разом
	 * запускался только телефон, а после его подтверждения окно закрывалось и
	 * черновик стирался: введённая почта не отправлялась никуда. Она ждёт здесь
	 * и подхватывается сразу после телефона.
	 */
	| { kind: 'confirm'; channel: 'phone' | 'email'; pendingValue: string; nextEmail?: string }

const editSchema = z.object({
	phone:    optionalPhone('contacts.editDialog.validation.phoneInvalid'),
	email:    z.string().trim().optional(),
	telegram: z.string().trim().optional(),
})

const confirmSchema = z.object({
	code: z.string().trim().min(4),
})

type EditContactsFormValues   = z.infer<typeof editSchema>
type ConfirmContactsFormValues = z.infer<typeof confirmSchema>

interface EditContactsDialogProps {
	accountId:    string
	phone?:       string | null
	email?:       string | null
	telegramId?:  string | null
	open:         boolean
	onOpenChange: (open: boolean) => void
}

export function EditContactsDialog({
	accountId, phone, email, telegramId, open, onOpenChange,
}: EditContactsDialogProps) {
	const { t } = useTranslation()
	const [step, setStep] = useState<Step>({ kind: 'edit' })
	const [submitError, setSubmitError] = useState<string | null>(null)

	const [initPhone,      { isLoading: isInitPhone      }] = useInitPhoneChangeMutation()
	const [confirmPhone,   { isLoading: isConfirmPhone   }] = useConfirmPhoneChangeMutation()
	const [initEmail,      { isLoading: isInitEmail      }] = useInitEmailChangeMutation()
	const [confirmEmail,   { isLoading: isConfirmEmail   }] = useConfirmEmailChangeMutation()
	const [updateTelegram, { isLoading: isUpdateTelegram }] = useUpdateTelegramMutation()

	const isLoading = isInitPhone || isConfirmPhone || isInitEmail || isConfirmEmail || isUpdateTelegram

	const { form: editForm, resetDraft } = usePersistedForm<EditContactsFormValues>({
		key:           `contacts:edit:${accountId}`,
		// Версии у аккаунта здесь нет — приходят только сами значения, и они же
		// служат версией: изменились телефон, почта или телеграм — черновик,
		// снятый до этого, устарел и не должен ложиться поверх свежих.
		recordVersion: `${phone ?? ''}|${email ?? ''}|${telegramId ?? ''}`,
		defaultValues: {
			phone:    phone      ?? '',
			email:    email      ?? '',
			telegram: telegramId ?? '',
		},
		resolver: zodResolverI18n(editSchema),
		mode:     'onBlur',
		active:   open && step.kind === 'edit',
	})

	const { form: confirmForm } = usePersistedForm<ConfirmContactsFormValues>({
		key:           `contacts:confirm:${accountId}`,
		defaultValues: { code: '' },
		resolver:      zodResolverI18n(confirmSchema),
		mode:          'onBlur',
		active:        open && step.kind === 'confirm',
	})

	function close() {
		setStep({ kind: 'edit' })
		setSubmitError(null)
		resetDraft()
		onOpenChange(false)
	}

	async function onEditSubmit(values: EditContactsFormValues) {
		setSubmitError(null)
		const trimPhone    = (values.phone    ?? '').trim()
		const trimEmail    = (values.email    ?? '').trim()
		const trimTelegram = (values.telegram ?? '').trim()
		const phoneChanged    = trimPhone    !== (phone      ?? '')
		const emailChanged    = trimEmail    !== (email      ?? '')
		const telegramChanged = trimTelegram !== (telegramId ?? '')

		try {
			if (telegramChanged) {
				await updateTelegram({ telegramId: trimTelegram || null }).unwrap().catch((err) => {
					console.warn('[contacts] updateTelegram skipped (no backend yet):', err)
				})
			}

			if (phoneChanged && trimPhone) {
				await initPhone({ phone: trimPhone }).unwrap()
				setStep({
					kind: 'confirm',
					channel: 'phone',
					pendingValue: trimPhone,
					nextEmail: emailChanged && trimEmail ? trimEmail : undefined,
				})
				return
			}
			if (emailChanged && trimEmail) {
				await initEmail({ email: trimEmail }).unwrap()
				setStep({ kind: 'confirm', channel: 'email', pendingValue: trimEmail })
				return
			}
			close()
		} catch (err) {
			console.error('[contacts] init change failed:', err)
			setSubmitError(t('settings.profile.contacts.initFailed', { defaultValue: 'Не удалось отправить код' }))
		}
	}

	async function onConfirmSubmit(values: ConfirmContactsFormValues) {
		if (step.kind !== 'confirm') return
		setSubmitError(null)
		try {
			if (step.channel === 'phone') {
				await confirmPhone({ code: values.code, phone: step.pendingValue }).unwrap()
				if (step.nextEmail) {
					await initEmail({ email: step.nextEmail }).unwrap()
					confirmForm.reset()
					setStep({ kind: 'confirm', channel: 'email', pendingValue: step.nextEmail })
					return
				}
			} else {
				await confirmEmail({ code: values.code, email: step.pendingValue }).unwrap()
			}
			close()
		} catch (err) {
			console.error('[contacts] confirm change failed:', err)
			setSubmitError(t('settings.profile.contacts.confirmFailed', { defaultValue: 'Неверный код' }))
		}
	}

	// ── edit step ───────────────────────────────────────────────
	if (step.kind === 'edit') {
		return (
			<Form {...editForm}>
				<FormDialog
					open={open}
					onOpenChange={(next) => { if (!next) close() }}
					title={t('settings.profile.subtabs.contacts')}
					onSubmit={editForm.handleSubmit(onEditSubmit)}
					isSubmitting={isLoading}
					submitLabel={t('common.save', { defaultValue: 'Сохранить' })}
					cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
					contentClassName="sm:max-w-md"
				>
					<div className="flex flex-col gap-3">
						<FormField
							control={editForm.control}
							name="phone"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('settings.profile.contact.phone')}</FormLabel>
									<FormControl>
										{isMaskSafePhone(phone) ? (
											<PhoneInput value={field.value ?? ''} onChange={field.onChange} />
										) : (
											<Input {...field} value={field.value ?? ''} />
										)}
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<TextField name="email"    label={t('settings.profile.contact.email')} type="email" />
						<TextField name="telegram" label={t('settings.profile.contact.telegram')} placeholder="@username" />
						<p className="text-xs text-(--color-muted-foreground)">
							{t('settings.profile.contacts.hint')}
						</p>
						{submitError && (
							<p className="text-sm text-(--color-destructive)">{submitError}</p>
						)}
					</div>
				</FormDialog>
			</Form>
		)
	}

	// ── confirm step ────────────────────────────────────────────
	const titleKey = step.channel === 'phone'
		? 'settings.profile.contacts.confirmPhoneTitle'
		: 'settings.profile.contacts.confirmEmailTitle'
	const hintKey = step.channel === 'phone'
		? 'settings.profile.contacts.confirmPhoneHint'
		: 'settings.profile.contacts.confirmEmailHint'

	return (
		<Form {...confirmForm}>
			<FormDialog
				open={open}
				onOpenChange={(next) => { if (!next) close() }}
				title={t(titleKey, { defaultValue: 'Подтверждение' })}
				onSubmit={confirmForm.handleSubmit(onConfirmSubmit)}
				isSubmitting={isLoading}
				submitLabel={t('common.confirm', { defaultValue: 'Подтвердить' })}
				cancelLabel={t('common.back', { defaultValue: 'Назад' })}
				contentClassName="sm:max-w-md"
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm text-(--color-muted-foreground)">
						{t(hintKey, {
							defaultValue: step.channel === 'phone'
								? `Код отправлен на ${step.pendingValue}`
								: `Код отправлен на ${step.pendingValue}`,
							value: step.pendingValue,
						})}
					</p>
					<TextField name="code" label={t('settings.profile.contacts.code', { defaultValue: 'Код' })} />
					{submitError && (
						<p className="text-sm text-(--color-destructive)">{submitError}</p>
					)}
				</div>
			</FormDialog>
		</Form>
	)
}
